/**
 * Phase 14.5 Step 4 — zod schema factories for the unified wizard.
 *
 * Bounds policy (refinement B):
 *   • Bounds that the live registries expose are READ from the
 *     registry payload at schema construction time:
 *       - geometry.run_budget_cap        → n_points upper cap
 *       - geometry.envelope_dimensions[] → per-dim lo/hi
 *       - geometry.sampler_strategies[]  → enum of valid kinds
 *   • Bounds the registries do NOT expose are HARDCODED here AND
 *     surfaced as tracked-drift entries in CURRENT_STATE.md
 *     (registry-cleanup queue).
 *
 * Orthogonality (refinement C): zod schemas check SCALAR / ARRAY /
 * STRING bounds. The dispatch helper checks toggle MUTUAL EXCLUSION.
 * The two error surfaces are independent and rendered separately in
 * the wizard — there is no overlap.
 */
import { z } from "zod";

// ─────────────────────────────────────────────────────────────────────
// Hardcoded bounds (pk-campaign-side; registry does not expose them)
// ─────────────────────────────────────────────────────────────────────
// Tracked in `/app/CURRENT_STATE.md` "Known drifts / cleanup queue":
// `pk-campaigns/registry` exposes per-variable bounds but does not
// surface scalar caps for `samples_per_variable`, `target_pk`,
// `max_iterations`, `samples_per_iteration`, `lambda_penalty`,
// or `name` length. Numbers below mirror the Phase 11 / 12 backend
// Pydantic constraints. Cleanup candidate: extend the registry to
// expose these caps under a `scalar_bounds` block.
//
// Phase 14.5 Step 6 (Y2e) — bounds re-aligned with the live
// `/api/openapi.json` Pydantic schemas. Pre-Y2e the frontend was
// MORE PERMISSIVE than the backend on three fields, surfacing
// 422s only after submit. Audit + closure tracked verbatim in
// CURRENT_STATE.md "Known drifts / cleanup queue" — Step 6 Y2e
// pre-fix audit findings block.
const PK_HARDCODED_BOUNDS = Object.freeze({
    name_min_length: 1,
    name_max_length: 120,
    samples_per_variable_min: 2,        // Y2e: was 1; backend Pydantic ge=2.
    samples_per_variable_max: 200,      // Y2e: was 500; backend Pydantic le=200.
    target_pk_min: 0.01,                // Backend exclusiveMinimum=0; frontend's 0.01 is narrower (acceptable).
    target_pk_max: 0.99,                // Backend exclusiveMaximum=1; frontend's 0.99 is narrower (acceptable).
    max_iterations_min: 1,
    max_iterations_max: 100,            // Y2e: was 200; backend Pydantic le=100.
    samples_per_iteration_min: 1,
    samples_per_iteration_max: 200,
    lambda_penalty_min: 0,
    lambda_penalty_max: 10,
    total_runs_cap: 5000,
});

// Phase 14.8 W3 — registry-driven bound resolution.
//
// `/api/pk-campaigns/registry` now exposes `scalar_bounds` introspected
// from the canonical Pydantic models (see backend
// `routers/pk_campaigns._pk_scalar_bounds_dict()`). When the registry
// payload includes a `scalar_bounds` field, schema factories below
// consume those values instead of the hardcoded mirrors. The
// hardcoded fallback stays in place for tests / offline mode and as a
// belt-and-braces back-compat shim if a future Pydantic refactor drops
// the registry surface.
function _resolveBounds(scalarBounds) {
    const sb = scalarBounds && typeof scalarBounds === "object" ? scalarBounds : {};
    const pick = (path, key, fallback) => {
        const v = sb[path]?.[key];
        return typeof v === "number" ? v : fallback;
    };
    return Object.freeze({
        // string lengths
        name_min_length: pick("pk_campaign_create.name", "min_length", PK_HARDCODED_BOUNDS.name_min_length),
        name_max_length: pick("pk_campaign_create.name", "max_length", PK_HARDCODED_BOUNDS.name_max_length),
        // sensitivity
        samples_per_variable_min: pick("sensitivity_config.samples_per_variable", "min", PK_HARDCODED_BOUNDS.samples_per_variable_min),
        samples_per_variable_max: pick("sensitivity_config.samples_per_variable", "max", PK_HARDCODED_BOUNDS.samples_per_variable_max),
        // target_pk: backend uses exclusive bounds; the frontend stays
        // SLIGHTLY narrower (0.01 / 0.99 vs 0.0 / 1.0) so the operator
        // never sends a degenerate value. Fallback preserves that.
        target_pk_min: PK_HARDCODED_BOUNDS.target_pk_min,
        target_pk_max: PK_HARDCODED_BOUNDS.target_pk_max,
        max_iterations_min: pick("target_pk_config.max_iterations", "min", PK_HARDCODED_BOUNDS.max_iterations_min),
        max_iterations_max: pick("target_pk_config.max_iterations", "max", PK_HARDCODED_BOUNDS.max_iterations_max),
        samples_per_iteration_min: pick("target_pk_config.samples_per_iteration", "min", PK_HARDCODED_BOUNDS.samples_per_iteration_min),
        samples_per_iteration_max: pick("target_pk_config.samples_per_iteration", "max", PK_HARDCODED_BOUNDS.samples_per_iteration_max),
        lambda_penalty_min: pick("target_pk_config.lambda_penalty", "min", PK_HARDCODED_BOUNDS.lambda_penalty_min),
        lambda_penalty_max: pick("target_pk_config.lambda_penalty", "max", PK_HARDCODED_BOUNDS.lambda_penalty_max),
        // total_runs_cap — pk_campaign_create.sample_count.max gives us this from the registry.
        total_runs_cap: pick("pk_campaign_create.sample_count", "max", PK_HARDCODED_BOUNDS.total_runs_cap),
    });
}

function _baseFieldsSchema(bounds) {
    return z.object({
        scenario_id: z.string().min(1, "scenario_id is required"),
        name: z.string()
            .min(bounds.name_min_length, "Name is required.")
            .max(bounds.name_max_length, `Name must be ≤ ${bounds.name_max_length} characters.`),
    });
}

// Hardcoded-bounds factories — exported as constants for back-compat
// with any direct importer (tests, harnesses) that doesn't pass a
// registry. New code should prefer `buildSubmitSchema(...)` which
// resolves bounds from the registry when available.
export const baseFieldsSchema = _baseFieldsSchema(PK_HARDCODED_BOUNDS);

// ─────────────────────────────────────────────────────────────────────
// Per-dimension schemas — each is invoked from `buildSubmitSchema`
// ─────────────────────────────────────────────────────────────────────

function _sensitivityConfigSchema(bounds) {
    return z.object({
        method: z.enum(["variance_first_order", "one_at_a_time"]),
        samples_per_variable: z.number().int()
            .min(bounds.samples_per_variable_min,
                 `samples_per_variable must be ≥ ${bounds.samples_per_variable_min}.`)
            .max(bounds.samples_per_variable_max,
                 `samples_per_variable must be ≤ ${bounds.samples_per_variable_max}.`),
        variable_subset: z.array(z.string()).optional().nullable(),
    }).refine(
        (v) => {
            const subsetSize = (v.variable_subset?.length) || 33;  // default-all is 33 for current registry
            return v.samples_per_variable * subsetSize <= bounds.total_runs_cap;
        },
        { message: `samples_per_variable × |variable_subset| exceeds the ${bounds.total_runs_cap}-run cap.` },
    );
}

export const sensitivityConfigSchema = _sensitivityConfigSchema(PK_HARDCODED_BOUNDS);

function _targetPkConfigSchema(bounds) {
    return z.object({
        target_pk: z.number()
            .min(bounds.target_pk_min)
            .max(bounds.target_pk_max),
        max_iterations: z.number().int()
            .min(bounds.max_iterations_min)
            .max(bounds.max_iterations_max),
        samples_per_iteration: z.number().int()
            .min(bounds.samples_per_iteration_min)
            .max(bounds.samples_per_iteration_max),
        lambda_penalty: z.number()
            .min(bounds.lambda_penalty_min)
            .max(bounds.lambda_penalty_max),
        variable_budget: z.array(z.string()).optional().nullable(),
    }).refine(
        (v) => v.max_iterations * v.samples_per_iteration <= bounds.total_runs_cap,
        { message: `max_iterations × samples_per_iteration exceeds the ${bounds.total_runs_cap}-run cap.` },
    );
}

export const targetPkConfigSchema = _targetPkConfigSchema(PK_HARDCODED_BOUNDS);

/**
 * Factory: build the geometry config zod schema from the LIVE
 * geometry registry payload. Registry-driven (refinement B):
 *   • n_points upper bound  ← geometryRegistry.run_budget_cap
 *   • sampler kind enum     ← geometryRegistry.sampler_strategies[].kind
 *   • envelope dim lo / hi  ← geometryRegistry.envelope_dimensions[].physical_bounds
 *   • envelope dim required ← geometryRegistry.envelope_dimensions[].id
 */
export function buildGeometryConfigSchema(geometryRegistry) {
    const reg = geometryRegistry || {};
    const cap = typeof reg.run_budget_cap === "number"
        ? reg.run_budget_cap
        : PK_HARDCODED_BOUNDS.total_runs_cap;
    const samplerKinds = Array.isArray(reg.sampler_strategies)
        ? reg.sampler_strategies.map((s) => s.kind).filter(Boolean)
        : [];
    const envelopeDims = Array.isArray(reg.envelope_dimensions)
        ? reg.envelope_dimensions
        : [];

    // RangeSpec per dim with registry-driven lo/hi bounds.
    const rangeSpec = (dim) => z.object({
        lo: z.number()
            .min(dim?.physical_bounds?.lo ?? -Infinity,
                 `${dim.id}.lo must be ≥ ${dim?.physical_bounds?.lo}`)
            .max(dim?.physical_bounds?.hi ?? Infinity,
                 `${dim.id}.lo must be ≤ ${dim?.physical_bounds?.hi}`),
        hi: z.number()
            .min(dim?.physical_bounds?.lo ?? -Infinity,
                 `${dim.id}.hi must be ≥ ${dim?.physical_bounds?.lo}`)
            .max(dim?.physical_bounds?.hi ?? Infinity,
                 `${dim.id}.hi must be ≤ ${dim?.physical_bounds?.hi}`),
    }).refine((r) => r.lo < r.hi, { message: `${dim.id}: lo must be strictly less than hi.` });

    const envelopeShape = {};
    for (const dim of envelopeDims) {
        envelopeShape[dim.id] = rangeSpec(dim);
    }

    return z.object({
        sampler: samplerKinds.length > 0
            ? z.enum(samplerKinds)
            : z.string().min(1),
        n_points: z.number().int()
            .min(1, "n_points must be at least 1.")
            .max(cap, `n_points exceeds the ${cap}-run cap.`),
        envelope: z.object(envelopeShape),
    });
}

export const degradationConfigSchema = z.object({
    profile_id: z.string().min(1, "Pick a degradation profile or turn the section off."),
});

// Phase 15.1 — GA optimisation config schema. Bounds mirror backend
// ``GAConfig`` Pydantic constraints in
// ``/app/backend/models/optimisation_campaign.py``.
const _GA_BOUNDS = Object.freeze({
    population_size_min: 8,
    population_size_max: 128,
    generations_min: 3,
    generations_max: 50,
    mc_seeds_min: 2,
    mc_seeds_max: 100,
    max_workers_min: 1,
    max_workers_max: 16,
    seed_min: 0,
    seed_max: 2147483647,
});

export const optimisationConfigSchema = z.object({
    population_size: z.number().int()
        .min(_GA_BOUNDS.population_size_min)
        .max(_GA_BOUNDS.population_size_max),
    generations: z.number().int()
        .min(_GA_BOUNDS.generations_min)
        .max(_GA_BOUNDS.generations_max),
    mc_seeds_per_individual: z.number().int()
        .min(_GA_BOUNDS.mc_seeds_min)
        .max(_GA_BOUNDS.mc_seeds_max),
    max_workers: z.number().int()
        .min(_GA_BOUNDS.max_workers_min)
        .max(_GA_BOUNDS.max_workers_max),
    seed: z.number().int()
        .min(_GA_BOUNDS.seed_min)
        .max(_GA_BOUNDS.seed_max),
});

export const _GA_BOUNDS_FOR_TEST = _GA_BOUNDS;

// ─────────────────────────────────────────────────────────────────────
// Composed wizard schema — invoked at submit-time
// ─────────────────────────────────────────────────────────────────────

/**
 * Build the submit-time zod schema for a given (lane, registries)
 * pair. Returns a `safeParse`-compatible schema.
 *
 * Phase 14.8 W3 — pulls scalar bounds from `registries.pk?.scalar_bounds`
 * (Pydantic-introspected at the backend) and falls back to the
 * hardcoded mirrors when the registry payload omits them. This keeps
 * operator-input validation byte-aligned with backend Pydantic without
 * requiring a frontend redeploy on every backend constraint tweak.
 *
 *   buildSubmitSchema("baseline", {pk, geometry, degradation})
 *   buildSubmitSchema("sensitivity", ...)
 *   buildSubmitSchema("target_pk", ...)
 *   buildSubmitSchema("geometry_sweep", ...)
 *
 * The returned schema validates the assembled POST payload (lane-
 * specific shape), NOT the wizard's form values. Pair with
 * `assemblePayload` from `lib/campaignDispatch.js`.
 */
export function buildSubmitSchema(lane, registries) {
    const bounds = _resolveBounds(registries?.pk?.scalar_bounds);
    const base = _baseFieldsSchema(bounds);
    if (lane === "baseline") {
        return base.extend({
            campaign_type: z.literal("baseline"),
            perturbation_overrides: z.record(z.string(), z.any()).optional(),
        });
    }
    if (lane === "sensitivity") {
        return base.extend({
            campaign_type: z.literal("sensitivity"),
            sensitivity_config: _sensitivityConfigSchema(bounds),
            perturbation_overrides: z.record(z.string(), z.any()).optional(),
        });
    }
    if (lane === "target_pk") {
        return base.extend({
            campaign_type: z.literal("target_pk"),
            target_pk_config: _targetPkConfigSchema(bounds),
            perturbation_overrides: z.record(z.string(), z.any()).optional(),
        });
    }
    if (lane === "geometry_sweep") {
        const geomSchema = buildGeometryConfigSchema(registries?.geometry);
        return base.extend({
            campaign_type: z.literal("geometry_sweep"),
            envelope: geomSchema.shape.envelope,
            sampler: z.object({
                kind: geomSchema.shape.sampler,
                strata_per_dim: z.number().int().nullable().optional(),
                levels_per_dim: z.number().int().nullable().optional(),
            }),
            n_points: geomSchema.shape.n_points,
            degradation_profile_id: z.string().nullable().optional(),
        });
    }
    if (lane === "optimisation") {
        // Optimisation lane has no `campaign_type` field — the backend
        // hardcodes it. Validates the GA config block.
        return base.extend({
            ga_config: optimisationConfigSchema,
        });
    }
    // Unknown lane — never validates.
    return z.never();
}

export const _PK_HARDCODED_BOUNDS_FOR_TEST = PK_HARDCODED_BOUNDS;
export const _resolveBoundsForTest = _resolveBounds;
